import type { BreadcrumbLink } from '$lib/features/_other/breadcrumbLink';
import type { Coordinates } from '$lib/features/_other/coordinates.type';
import type { Faq } from '$lib/features/_other/faq';
import type { CountryPreview } from '$lib/features/country/countryPreview.type';
import { type ImageDetails } from '$lib/features/image/imageDetails.type';
import { WIFI_ACCESS_LABELS, type SimplePlace } from '$lib/features/place/place.type';
import { FULL_SITE_URL } from '$lib/statics';
import { capitalizeWords } from '$lib/utils/general.util';
import { buildFacebookLink, buildInstagramLink, buildUnescoLink } from '$lib/utils/officialLinks.util';
import { UrlUtil, type NavLink } from '$lib/utils/url.util';

export type WithContext<T> = T & { '@context': 'https://schema.org' };
type ItemUrl = string;

export interface SchemaCountry {
    '@type': 'Country';
    name: string;
    alternateName?: string;
    url: ItemUrl;
    image?: ItemUrl;
}

export interface SchemaCity {
    '@context'?: 'https://schema.org';
    '@type': 'City';
    name: string;
    alternateName?: string;
    url: string;
    image: ItemUrl | SchemaImageObject | SchemaImageObject[];
}

export interface SchemaPlace {
    '@type': 'Place';
    geo: SchemaGeoCoordinates;
}

export interface SchemaThing {
    '@type': 'Thing';
    name: string;
    alternateName?: string;
    description: string;
    image?: ItemUrl | SchemaImageObject | SchemaImageObject[];
    url?: ItemUrl;
}

export interface SchemaGeoCoordinates {
    '@type': 'GeoCoordinates';
    latitude: number;
    longitude: number;
    elevation?: number;
}

export interface SchemaCollectionPage<TEntity> {
    '@type': 'CollectionPage';
    name?: string;
    description?: string;
    url: ItemUrl;
    mainEntity: SchemaList<SchemaListItem<TEntity>>;
}

export interface PropertyValue {
    '@type': 'PropertyValue';
    name: string;
    value: string | number;
}

export interface LocationFeatureSpecification {
    '@type': 'LocationFeatureSpecification';
    name: string;
    value: boolean;
}

export interface SchemaTouristAttraction {
    '@context'?: 'https://schema.org';
    '@type': 'TouristAttraction' | string[];
    name: string;
    alternateName?: string;
    description?: string;
    url?: ItemUrl;             // This will be the primaryLink if it exists
    image?: ItemUrl | SchemaImageObject | SchemaImageObject[];
    geo?: SchemaGeoCoordinates;
    sameAs?: string | string[]; // Strictly for Wikidata / Official external sites
    isAccessibleForFree?: boolean;
    publicAccess: boolean;
    amenityFeature?: LocationFeatureSpecification[];
    additionalProperty?: PropertyValue[];
}

export interface SchemaTouristTrip {
    '@type': 'TouristTrip';
    name?: string;
    description?: string;
    itinerary: SchemaList<SchemaListItem<SchemaTouristAttraction>>;
}

export interface SchemaImageObject {
    '@type': 'ImageObject';
    contentUrl: ItemUrl;
    caption: string;
    width: number;
    height: number;
    creator: {
        '@type': 'Person';
        name: string;
    };
    license: string;
    acquireLicensePage: ItemUrl;
    copyrightNotice: string;
    creditText: string;
    contentLocation?: SchemaPlace;
    representativeOfPage: boolean;
    isFamilyFriendly: boolean;
}

export interface SchemaFAQPage {
    '@type': 'FAQPage';
    mainEntity: SchemaQuestion[];
}

export interface SchemaQuestion {
    '@type': 'Question';
    name: string;
    acceptedAnswer: SchemaAnswer;
}

export interface SchemaAnswer {
    '@type': 'Answer';
    text: string;
}

export interface SchemaListItem<TItem = string | SchemaCity | SchemaThing | SchemaTouristAttraction> {
    '@type': 'ListItem';
    position: number;
    name?: string;
    item: TItem;  // Can be a URL string or a nested schema object
}

export interface SchemaBreadcrumbList {
    '@type': 'BreadcrumbList';
    itemListElement: SchemaListItem<string>[];
}

export interface SchemaList<TItem> {
    '@type': 'ItemList';
    name?: string;
    description?: string;
    itemListElement: TItem[];
}

// --- GENERIC BUILDERS ---

export function buildBreadcrumbList(breadcrumbs: BreadcrumbLink[]): WithContext<SchemaBreadcrumbList> {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.label,
            item: `${FULL_SITE_URL}/${crumb.nav.href}`
        }))
    };
}

export function buildList<TItem = SchemaListItem>({ name, description, items }: { name?: string, description?: string, items: TItem[] }): SchemaList<TItem> {
    return {
        '@type': 'ItemList',
        name,
        description,
        itemListElement: items
    };
}

export function buildListItem<T>(item: T, position: number, name?: string): SchemaListItem<T> {
    return {
        '@type': 'ListItem',
        position,
        ...(name ? { name } : {}),
        item
    };
}

export function buildCollectionPage<T>(
    { name, description, nav, items }: { name?: string, description?: string, nav: NavLink, items: SchemaListItem<T>[] }
): WithContext<SchemaCollectionPage<T>> {
    return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name,
        description,
        url: `${FULL_SITE_URL}/${nav.href}`,
        mainEntity: buildList({ items: items })
    };
}

export function buildThing({ name, alternateName, description, image, nav }: { name: string, alternateName: string | null, description: string, image?: ItemUrl | ImageDetails | ImageDetails[], nav?: NavLink }): WithContext<SchemaThing> {
    let schemaImage: ItemUrl | SchemaImageObject | SchemaImageObject[] | undefined = undefined;
    if (typeof image === 'string') {
        schemaImage = image;
    } else if (Array.isArray(image)) {
        if (image.length > 0) {
            schemaImage = [];
            for (let i = 0; i < image.length; i++) {
                schemaImage.push(buildImageObject(image[i], false));
            }
        }
    } else if (image !== undefined) {
        schemaImage = buildImageObject(image, false);
    }
    return {
        '@context': 'https://schema.org',
        '@type': 'Thing',
        name: name,
        alternateName: alternateName || undefined,
        description: description,
        image: schemaImage,
        url: nav ? `${FULL_SITE_URL}/${nav?.href}` : undefined
    };
}

export function buildImageObject(image: ImageDetails, isRepresentativeOfPage: boolean): SchemaImageObject {
    return {
        '@type': 'ImageObject',
        contentUrl: UrlUtil.getFullImageUrl(image),
        caption: image.altText,
        width: image.width,
        height: image.height,
        creator: {
            "@type": "Person",
            name: image.authorUsername
        },
        license: image.licence.link,
        acquireLicensePage: `https://commons.wikimedia.org/w/index.php?curid=${image.wikiId}`,
        copyrightNotice: `© ${image.authorUsername}`,
        creditText: `${image.authorUsername} / Wikimedia Commons`,
        contentLocation: image.latitude && image.longitude ? buildPlace({ coordinates: { latitude: image.latitude, longitude: image.longitude } }) : undefined,
        representativeOfPage: isRepresentativeOfPage,
        isFamilyFriendly: true,
    };
}

export function buildTouristAttraction({
    englishName,
    nativeName,
    description,
    place,
    image,
    nav,
    options = { withContext: false } }: {
        englishName: string,
        nativeName?: string | null,
        description?: string,
        place: SimplePlace | null | undefined,
        image?: ItemUrl | ImageDetails | ImageDetails[];
        nav?: NavLink,
        options?: { isAccessibleForFree?: boolean, additionalType?: string, withContext?: boolean }
    }): SchemaTouristAttraction {
    let schemaImage: ItemUrl | SchemaImageObject | SchemaImageObject[] | undefined = undefined;
    if (typeof image === 'string') {
        schemaImage = image;
    } else if (Array.isArray(image)) {
        if (image.length > 0) {
            schemaImage = [];
            for (let i = 0; i < image.length; i++) {
                schemaImage.push(buildImageObject(image[i], i === 0));
            }
        }
    } else if (image !== undefined) {
        schemaImage = buildImageObject(image, false);
    }

    const sameAsList = [
        place?.wikidataId ? `https://www.wikidata.org/wiki/${place.wikidataId}` : null,
        place?.websiteUrl,
        place?.unescoId ? buildUnescoLink(place.unescoId, false) : null,
        place?.instagramId ? buildInstagramLink(place.instagramId, false) : null,
        place?.facebookId ? buildFacebookLink(place.facebookId, false) : null,
    ].filter((x): x is string => Boolean(x));
    const amenityFeatures = place ? buildAmenityFeatures(place) : undefined;
    const additionalProperties = place ? buildAdditionalProperties(place) : undefined;

    return {
        '@context': options?.withContext ? 'https://schema.org' : undefined,
        '@type': options?.additionalType ? ['TouristAttraction', options.additionalType] : 'TouristAttraction',
        name: englishName,
        alternateName: nativeName || undefined,
        description: description,
        url: nav ? `${FULL_SITE_URL}/${nav.href}` : undefined,
        image: schemaImage,
        geo: place ? buildSchemaGeoCoordinates(place.coordinates, { elevationMeters: place.elevationMeters }) : undefined,
        sameAs: sameAsList.length ? sameAsList : undefined,
        isAccessibleForFree: options?.isAccessibleForFree,
        publicAccess: true,
        amenityFeature: amenityFeatures?.length ? amenityFeatures : undefined,
        additionalProperty: additionalProperties?.length ? additionalProperties : undefined
    };
}

export function buildPlace(place: { coordinates: Coordinates }): SchemaPlace {
    return {
        '@type': 'Place' as const,
        geo: buildSchemaGeoCoordinates(place.coordinates),
    };
}

export function buildSchemaGeoCoordinates(coordinates: Coordinates, other?: { elevationMeters?: number | null }): SchemaGeoCoordinates {
    return {
        '@type': 'GeoCoordinates' as const,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        elevation: other?.elevationMeters ?? undefined
    };
}

export function buildTouristTrip({ name, description, itinerary }: { name?: string, description?: string, itinerary: SchemaList<SchemaListItem<SchemaTouristAttraction>> },): WithContext<SchemaTouristTrip> {
    return {
        '@context': 'https://schema.org',
        '@type': 'TouristTrip',
        name,
        description,
        itinerary: itinerary
    };
}

export function buildCountry(country: CountryPreview): SchemaCountry {
    return {
        '@type': 'Country',
        name: country.englishName,
        alternateName: country.nativeName || undefined,
        url: `${FULL_SITE_URL}/${UrlUtil.toCountry({ countrySlug: country.slug }).href}`,
        image: country.images.length > 0 ? UrlUtil.getFullImageUrl(country.images[0]) : undefined
    };
}

export function buildCity({ englishName, alternateName, image, nav, options = { withContext: false } }: { englishName: string, alternateName: string | null, image: ItemUrl | ImageDetails | ImageDetails[], nav: NavLink, options?: { withContext?: boolean } }): SchemaCity {
    let schemaImage: ItemUrl | SchemaImageObject | SchemaImageObject[] = [];
    if (typeof image === 'string') {
        schemaImage = image;
    } else if (Array.isArray(image)) {
        for (let i = 0; i < image.length; i++) {
            schemaImage.push(buildImageObject(image[i], i === 0));
        }
    } else {
        schemaImage = buildImageObject(image, false);
    }
    return {
        '@context': options?.withContext ? 'https://schema.org' : undefined,
        '@type': 'City',
        name: englishName,
        alternateName: alternateName || undefined,
        url: `${FULL_SITE_URL}/${nav.href}`,
        image: schemaImage
    };
}

export function buildFAQPage(faqs: Faq[]): WithContext<SchemaFAQPage> {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
            }
        }))
    };
}

// --- HELPER FUNCTION ---

/** Safely serializes JSON-LD to prevent XSS attacks when using {@html ...} */
export function serializeJsonLd(data: unknown): string {
    return JSON.stringify(data).replace(/</g, '\\u003c');
}

function buildAdditionalProperties(place: SimplePlace): PropertyValue[] {
    const props: PropertyValue[] = [];

    if (place.inceptionYear) props.push({ '@type': 'PropertyValue', name: 'Year Built', value: place.inceptionYear });
    if (place.architects?.length) props.push({ '@type': 'PropertyValue', name: 'Architect', value: place.architects.join(', ') });
    if (place.architecturalStyles?.length) props.push({ '@type': 'PropertyValue', name: 'Architectural Style', value: place.architecturalStyles.join(', ') });
    if (place.founders?.length) props.push({ '@type': 'PropertyValue', name: 'Founder', value: place.founders.join(', ') });
    if (place.heritageDesignations?.length) props.push({ '@type': 'PropertyValue', name: 'Heritage Designation', value: place.heritageDesignations.join(', ') });
    if (place.exhibitionSize) props.push({ '@type': 'PropertyValue', name: 'Exhibition Size', value: place.exhibitionSize });
    if (place.visitorsPerYear) {
        const suffix = place.visitorsPerYearAsOf ? ` (as of ${place.visitorsPerYearAsOf})` : '';
        props.push({ '@type': 'PropertyValue', name: 'Visitors Per Year', value: `${place.visitorsPerYear}${suffix}` });
    }
    if (place.cost) props.push({ '@type': 'PropertyValue', name: 'Cost', value: `${place.cost.amount} ${place.cost.currencyCode}` });

    return props;
}

function buildAmenityFeatures(place: SimplePlace): LocationFeatureSpecification[] {
    const facilities = place.facilities?.map((f) => toFeature(f)) ?? [];
    const accessibility = dedupeAccessibility(place.accessibilityProperties ?? []);
    const wifi =
        place.wifiAccess && place.wifiAccess !== 'no'
            ? [toFeature(place.wifiAccess === 'yes' ? 'Wi-Fi' : `Wi-Fi (${WIFI_ACCESS_LABELS[place.wifiAccess]})`)]
            : [];
    return [...facilities, ...accessibility, ...wifi];
}

type AccessibilityParse = { subject: string; value: boolean; partial: boolean };

function parseAccessibility(raw: string): AccessibilityParse {
    const s = raw.trim();

    let m = /^accessible for (.+)$/i.exec(s);
    if (m) return { subject: `Accessible for ${m[1]}`, value: true, partial: false };

    m = /^(.+?)\s+inaccessible$/i.exec(s);
    if (m) return { subject: `${capitalizeWords(m[1])} Accessible`, value: false, partial: false };

    m = /^(.+?)\s+partially accessible$/i.exec(s);
    if (m) return { subject: `${capitalizeWords(m[1])} Accessible`, value: true, partial: true };

    m = /^(.+?)\s+accessible$/i.exec(s);
    if (m) return { subject: `${capitalizeWords(m[1])} Accessible`, value: true, partial: false };

    return { subject: capitalizeWords(s), value: true, partial: false };
}

// If Wikidata ever gives us two conflicting claims for the same feature (e.g. both "wheelchair accessible" and "wheelchair partially accessible" on one place), keep only one rather than emitting two entries with the same name and contradictory values. "Partial" wins over a flat true/false, since it's the most specific and least likely to overstate accessibility.
function dedupeAccessibility(raw: string[]): LocationFeatureSpecification[] {
    const bySubject = new Map<string, AccessibilityParse>();
    for (const r of raw) {
        const parsed = parseAccessibility(r);
        const key = parsed.subject.toLowerCase();
        const existing = bySubject.get(key);
        if (!existing || parsed.partial) {
            bySubject.set(key, parsed);
        }
    }
    return [...bySubject.values()].map(({ subject, value, partial }) =>
        toFeature(partial ? `${subject} (Partial)` : subject, value)
    );
}

function toFeature(name: string, value = true): LocationFeatureSpecification {
    return { '@type': 'LocationFeatureSpecification', name, value };
}
